import { useEffect, useMemo, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { schedulerFiltersApi } from "common/api/scheduler-filters";
import type { Entity } from "common/entities/types";
import { isQueryEquals } from "common/query-builder/functions";
import type { Context } from "common/types/context";
import { CancellablePromise } from "common/types/promises";
import { SchedulerFilter } from "common/types/scheduler-filters";
import { getErrorMessage } from "common/ui/error/functions";
import { showErrorNotification } from "common/ui/notification";
import { DebounceInputWithSearch } from "common/widgets/input-with-search";
import type { ValueProps } from "common/with-value-for";
import { SaveFilterModal } from "x/records/list/filter/modal";
import { SaveFilterState } from "x/records/list/types";
import { getDefaultFilter } from "../functions/query";
import { ConfigurationActions } from "./actions";
import { EditConfiguration } from "./edit";
import { ConfigurationSelector } from "./selector";

import "./configuration.scss";

interface ConfigurationType extends ValueProps<SchedulerFilter> {
  workOrderEntity: Entity;
  context: Context;
  searchValue: string;
  onSearchValueChange: (searchValue: string) => void;
}

export const Configuration = ({
  context,
  workOrderEntity,
  searchValue,
  onSearchValueChange,
  value,
  onChange,
}: ConfigurationType) => {
  const [filters, setFilters] = useState<SchedulerFilter[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalValue, setModalValue] = useState<SchedulerFilter>();
  const [saveFilterState, setSaveFilterState] = useState<SaveFilterState>();

  const savedFiltersRequest = useRef<CancellablePromise<unknown>>();

  const defaultFilter = useMemo(
    () => getDefaultFilter(context, workOrderEntity),
    [context, workOrderEntity],
  );

  useEffect(() => {
    savedFiltersRequest.current = schedulerFiltersApi(context.apiCall)
      .listForEntity(workOrderEntity.name)
      .then((savedFilters = []) => {
        setFilters([defaultFilter, ...savedFilters]);
      });

    return () => {
      savedFiltersRequest.current?.cancel();
    };
  }, [context.apiCall, workOrderEntity.name, defaultFilter]);

  const savedSelectedFilter = useMemo(
    () => filters.find((filter) => filter.id === value?.id),
    [filters, value?.id],
  );

  const isSelectedFilterModified = useMemo(
    () =>
      savedSelectedFilter &&
      !isQueryEquals(
        context.entities,
        workOrderEntity,
        value?.query,
        savedSelectedFilter?.query,
      ),
    [context.entities, workOrderEntity, value?.query, savedSelectedFilter],
  );

  const isDefaultFilter = useMemo(
    () =>
      isQueryEquals(
        context.entities,
        workOrderEntity,
        savedSelectedFilter?.query,
        defaultFilter?.query,
      ),
    [
      context.entities,
      workOrderEntity,
      savedSelectedFilter?.query,
      defaultFilter?.query,
    ],
  );

  const onSaveViewModalShow = (state: SaveFilterState) => {
    const newModalValue =
      state === "new" ? { ...value, name: undefined, shared: false } : value;

    unstable_batchedUpdates(() => {
      setModalValue(newModalValue);
      setSaveFilterState(state);
      setShowModal(true);
    });
  };

  const onDeleteFilter = () => {
    if (!value?.id) return;

    schedulerFiltersApi(context.apiCall)
      .remove(value?.id)
      .then(() => {
        setFilters((prevFilters) =>
          prevFilters.filter((f) => f.id !== value?.id),
        );
        onChange(defaultFilter);
      })
      .finally(() => setShowModal(false));
  };

  const onResetFilter = () => {
    onChange(savedSelectedFilter);
  };

  const closeSaveFilterModal = () => {
    setShowModal(false);
  };

  const onSaveNewFilter = () => {
    const request =
      saveFilterState === "new"
        ? schedulerFiltersApi(context.apiCall)
            .create(modalValue)
            .then((response) => {
              const savedFilter = { ...modalValue, id: response.id };
              setFilters((prevFilters) => [...prevFilters, savedFilter]);
              onChange(savedFilter);
            })
        : schedulerFiltersApi(context.apiCall)
            .update(modalValue)
            .then(() => {
              setFilters((prevFilters) =>
                prevFilters.map((f) =>
                  f.id === modalValue.id ? modalValue : f,
                ),
              );
              onChange(modalValue);
            });

    request
      .catch((e) => {
        const errorMessages = getErrorMessage(e);
        const errorMessage = Array.isArray(errorMessages)
          ? errorMessages.join(" ")
          : errorMessages;
        showErrorNotification(errorMessage);
      })
      .finally(() => setShowModal(false));
  };

  const onChangeModalValue = (newModalValue: SchedulerFilter) => {
    setModalValue(newModalValue);
  };

  return (
    <div className="x-scheduler-work-orders-configuration">
      <div className="x-scheduler-filters">
        <ConfigurationSelector
          context={context}
          filters={filters}
          filter={value}
          onFilterSelected={onChange}
          isFilterChanged={isSelectedFilterModified}
        />
        <EditConfiguration
          context={context}
          value={value}
          onChange={onChange}
        />
        <ConfigurationActions
          context={context}
          selectedFilter={savedSelectedFilter}
          isSelectedFilterModified={isSelectedFilterModified}
          isDefaultFilter={isDefaultFilter}
          onSaveViewModalShow={onSaveViewModalShow}
          onFilterReset={onResetFilter}
        />
        {showModal && (
          <SaveFilterModal
            key="save-filter-modal"
            onSave={onSaveNewFilter}
            onCancel={closeSaveFilterModal}
            onDelete={onDeleteFilter}
            saveFilterState={saveFilterState}
            context={context}
            disableGranularShare={true}
            value={modalValue}
            onChange={onChangeModalValue}
          />
        )}
      </div>
      <DebounceInputWithSearch
        value={searchValue}
        onChange={onSearchValueChange}
      />
    </div>
  );
};
